import {
  createContext,
  Dispatch,
  ReactNode,
  Reducer,
  SetStateAction,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'

import {
  SanityPrintPermission,
  SanityPrintSlotSettings,
} from '@data/sanity/queries/types/print'
import { SanityPrintShop } from '@data/sanity/queries/types/shop'
import { getShopFormSteps, parseShopPrints, shopStateReducer } from './shop'
import { ShopAction, ShopFormMode, ShopFormStep, ShopState } from './types'

interface PrintShopFormContextProps {
  printSlots: SanityPrintSlotSettings[]
  printPermissions: SanityPrintPermission[]
  activeStepIndex: number
  shopFormSteps: ShopFormStep[]
  setActiveStepIndex: Dispatch<SetStateAction<number>>
  shopState: ShopState
  dispatchShopState: Dispatch<ShopAction>
  shopFormMode: ShopFormMode
  setShopFormMode: Dispatch<SetStateAction<ShopFormMode>>
}

interface PrintShopFormContextProviderProps {
  printSlots: SanityPrintSlotSettings[]
  printPermissions: SanityPrintPermission[]
  shop?: SanityPrintShop
  initialStepIndex?: number
  children?: ReactNode
}

export const PrintShopFormContext = createContext<PrintShopFormContextProps>({
  printSlots: [],
  printPermissions: [],
  activeStepIndex: 0,
  shopFormSteps: [],
  setActiveStepIndex: () => null,
  shopState: {},
  dispatchShopState: () => null,
  shopFormMode: 'create',
  setShopFormMode: () => null,
})

export const PrintShopFormContextProvider = ({
  printSlots,
  printPermissions,
  shop,
  initialStepIndex,
  children,
}: PrintShopFormContextProviderProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0)
  const [shopFormMode, setShopFormMode] = useState<ShopFormMode>('create')

  const [shopState, dispatchShopState] = useReducer<
    Reducer<ShopState, ShopAction>
  >(shopStateReducer, {
    id: shop?.id,
    slug: shop?.slug,
    shopName: shop?.name,
    institutionName: shop?.institution?.name,
    prints: parseShopPrints(shop?.prints ?? []),
    userEmail: shop?.userEmail,
  })

  const shopFormSteps = useMemo(() => {
    return getShopFormSteps(printSlots)
  }, [printSlots])

  // Set active step index when initial step index changes
  useEffect(() => {
    if (typeof initialStepIndex === 'undefined') {
      return
    }

    setActiveStepIndex(initialStepIndex)
  }, [initialStepIndex])

  return (
    <PrintShopFormContext.Provider
      value={{
        printSlots,
        printPermissions,
        activeStepIndex,
        shopFormSteps,
        setActiveStepIndex,
        shopState,
        dispatchShopState,
        shopFormMode,
        setShopFormMode,
      }}
    >
      {children}
    </PrintShopFormContext.Provider>
  )
}
